import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { CommonSelect } from '@/features/campaigns/components/CommonSelect';
import { useClientsQuery } from '@/features/clients/api/useClientsQueries';
import type { ClientUser } from '@/features/clients/api/clientsApi';
import { CreateClientModal } from '@/features/clients/pages/AddClientPage/sections/CreateClientModal';
import { useMyAssignedClientsQuery } from '@/features/team/api/useTeamQueries';
import { useAppSelector } from '@/shared/store/hooks';
import {
  isAgencyRole,
  isTeamMemberRole,
  requiresAgencyClient,
} from '@/shared/config/roles';

function companyName(c: ClientUser) {
  return (
    (typeof c.companyName === 'string' && c.companyName.trim()) ||
    (typeof c.entityName === 'string' && c.entityName.trim()) ||
    ''
  );
}

function contactPersonName(c: ClientUser) {
  return [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
}

/** Prefer "Company · Contact person"; fall back to whichever is available. */
function clientLabel(c: ClientUser) {
  const company = companyName(c);
  const contact = contactPersonName(c);
  if (company && contact && company.toLowerCase() !== contact.toLowerCase()) {
    return `${company} · ${contact}`;
  }
  return company || contact || c.email || 'Unnamed client';
}

function isClientVerified(c: ClientUser) {
  const status = String(c.status || c.isAdminVerified || '').toUpperCase();
  return status === 'VERIFIED' || status === 'TRUE';
}

function resolveClients(data: unknown): ClientUser[] {
  if (Array.isArray(data)) return data as ClientUser[];
  if (data && typeof data === 'object') {
    const d = data as {
      users?: ClientUser[];
      clients?: ClientUser[];
      data?: ClientUser[];
    };
    if (Array.isArray(d.users)) return d.users;
    if (Array.isArray(d.clients)) return d.clients;
    if (Array.isArray(d.data)) return d.data;
  }
  return [];
}

type AgencyClientSelectProps = {
  value: string;
  error?: string;
  onChange: (clientId: string) => void;
};

/**
 * Client picker for Ad Agency and Team Members.
 * - Agency: all agency clients via /clients (+ create client inline)
 * - Team member: only assigned clients via /team-members/my-assigned-clients
 */
export function AgencyClientSelect({
  value,
  error,
  onChange,
}: AgencyClientSelectProps) {
  const role = useAppSelector((s) => s.auth.user?.role);
  const isAgency = isAgencyRole(role);
  const isTeamMember = isTeamMemberRole(role);
  const showPicker = requiresAgencyClient(role);
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingSelectId, setPendingSelectId] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const agencyListQ = useClientsQuery(
    { page: 1, limit: 100 },
    { enabled: isAgency },
  );
  const assignedListQ = useMyAssignedClientsQuery(
    { page: 1, limit: 100 },
    { enabled: isTeamMember },
  );

  const listQ = isTeamMember ? assignedListQ : agencyListQ;
  const isLoading = listQ.isLoading;

  const options = useMemo(() => {
    if (!showPicker) return [];
    const clients = resolveClients(listQ.data?.data ?? listQ.data).filter(
      isClientVerified,
    );
    return clients.map((c) => {
      const label = clientLabel(c);
      const company = companyName(c);
      const contact = contactPersonName(c);
      return {
        key: c._id,
        label,
        searchText: [company, contact, label, c.email].filter(Boolean).join(' '),
        tag: 'Verified',
        tagTone: 'success' as const,
      };
    });
  }, [showPicker, listQ.data]);

  useEffect(() => {
    if (!value) return;
    // Don't clear the selection while the client list is still loading —
    // otherwise going Back to Step 1 wipes agencyClientId as soon as this mounts.
    if (isLoading || !listQ.isFetched) return;
    const selected = options.find((o) => String(o.key) === String(value));
    if (!selected) onChange('');
  }, [value, options, onChange, isLoading, listQ.isFetched]);

  useEffect(() => {
    if (!pendingSelectId) return;
    const selected = options.find((o) => String(o.key) === String(pendingSelectId));
    if (selected) onChange(pendingSelectId);
    setPendingSelectId(null);
  }, [pendingSelectId, options, onChange]);

  const openCreateClient = () => {
    setDropdownOpen(false);
    setCreateOpen(true);
  };

  if (!showPicker) return null;

  return (
    <>
      <CommonSelect
        label="Client"
        name="agencyClientId"
        value={value}
        options={options}
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        placeholder={
          isLoading
            ? 'Loading clients…'
            : isTeamMember
              ? 'Select an assigned client'
              : 'Select a client'
        }
        error={error}
        required
        searchable
        hint={
          isTeamMember
            ? 'Only clients assigned to you can be selected'
            : 'Only verified clients can be selected for a campaign'
        }
        footer={
          isAgency ? (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openCreateClient();
              }}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#EAF3F8] px-3 py-2.5 text-sm font-semibold text-[#145E8C] transition hover:bg-[#dcecf4]"
            >
              <Plus className="h-4 w-4" />
              Create client
            </button>
          ) : null
        }
        action={
          isAgency ? (
            <button
              type="button"
              onClick={openCreateClient}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--dash-brand)] transition hover:underline"
            >
              <Plus className="h-3.5 w-3.5" />
              Create new client
            </button>
          ) : null
        }
        onChange={(_name, next) => onChange(next)}
      />

      {isAgency ? (
        <CreateClientModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={({ client, requiresVerification }) => {
            void listQ.refetch();
            if (isClientVerified(client) && !requiresVerification) {
              setPendingSelectId(client._id);
            }
          }}
        />
      ) : null}
    </>
  );
}
